import { useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import Highcharts from 'highcharts'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'
import { calcIgnorePercents } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const scopeToRootId = scope => (scope || []).filter(v => v != null && v !== '*').join('.')

const ComboChart = ({
  questionId,
  questionKey,
  qType,
  viewMode,
  answerVariants,
  answer,
  scope,
  onScopeChange,
  format,
  colorTheme,
  chartLegendMode,
  features
}) => {
  const chartMode = viewMode

  const ignoreRefs = format?.ignoreRefs === true
  const ignorePercents = calcIgnorePercents(format)

  const chartRef = useRef(null)

  // Read by handlers the chart holds on to, which outlive the render that bound them.
  const rootIdRef = useRef('')
  rootIdRef.current = scopeToRootId(scope)

  // The last level the chart moved to on its own account, recorded unbuffered -- the move the
  // block acts on is debounced, so between the two the chart is ahead of the scope.
  const chartRootRef = useRef(null)

  // The level already issued to the chart on screen. Issuing redraws, so the same level is never
  // issued twice over -- the redraw it causes would land on the draw it just asked for.
  const issuedRef = useRef(null)

  // Set while the redraw the component itself asked for is on its way, so it is not read as the
  // chart having redrawn for reasons of its own.
  const ownRedrawRef = useRef(false)

  const restoreRoot = useCallback(chart => {
    const rootId = rootIdRef.current
    const series = chart?.series?.[0]
    if (!series || issuedRef.current === rootId) return

    const shown = String(series.rootNode || '')
    const announced = chartRootRef.current

    // A level the chart moved to itself holds until the scope catches up, or a restore would undo
    // the researcher's own drill. Anywhere else -- a fresh chart on reload, a view swapped for
    // another, a rebuilt tree drawn from the top while the series still names the level -- the
    // stored level is (re-)issued.
    if (shown !== String(rootId) && null != announced && shown === String(announced)) return

    // the top is a level too: a revert to it has to take a drilled chart back up, and the top has
    // no point of its own to look up
    if (!rootId) {
      if (!shown) return

      issuedRef.current = rootId
      ownRedrawRef.current = true
      series.setRootNode('', true, { trigger: 'click' })
      return
    }

    const point = chart.get(rootId)
    // a level with nothing under it cannot be a root -- the chart would drop straight back out
    if (!point || !series.nodeMap?.[rootId]?.children?.length) return

    issuedRef.current = rootId
    ownRedrawRef.current = true
    point.series.setRootNode(point.id, true, { trigger: 'click' })
  }, [])

  // Mutating the chart from inside its own load leaves it half-drawn, so the level waits a frame.
  const chartLoad = useCallback(
    event => {
      const chart = event?.target
      const series = chart?.series?.[0]

      chartRootRef.current = null
      issuedRef.current = null
      ownRedrawRef.current = false
      if (!chart) return

      if (series) {
        Highcharts.addEvent(series, 'setRootNode', e => {
          // A move to the level the scope already names is the component's own restore (the
          // event fires from inside the call), not the chart getting ahead of the scope.
          if (String(e.newRootId || '') === String(rootIdRef.current)) return
          chartRootRef.current = e.newRootId
          issuedRef.current = null
        })
      }

      // The chart redraws for reasons of its own -- the width it measures after mounting, a
      // payload, an option update from a re-render -- and each of those rebuilds its tree from the
      // top, undoing the level a moment after it was drawn. Its own redraws are the only signal
      // that this happened, so the level is put back after every redraw the component did not ask
      // for; the one it did ask for is skipped, or the two would drive each other.
      Highcharts.addEvent(chart, 'redraw', () => {
        if (ownRedrawRef.current) {
          ownRedrawRef.current = false
          return
        }

        issuedRef.current = null
        requestAnimationFrame(() => restoreRoot(chart))
      })

      requestAnimationFrame(() => restoreRoot(chart))
    },
    [restoreRoot]
  )

  const setRootNode = useCallback(
    event => {
      // A root change carrying no trigger is the chart's own: rebuilding its tree, it could not
      // place the level the researcher is on and dropped back to the top. Writing that down would
      // lose their drill on the next redraw, so the level is put back instead -- on the following
      // frame, once the rebuilt tree has settled.
      if (!event.trigger) {
        requestAnimationFrame(() => restoreRoot(chartRef.current?.chart))
        return
      }

      const filtered = (scope || []).filter(v => v != null && v !== '*')

      if (filtered.join('.') === event.newRootId) return

      if (event.newRootId.length > 0) {
        const newScope = event.newRootId
          .split('.')
          .map(v => parseInt(v, 10))
          .concat(['*'])
        onScopeChange(newScope)
      } else {
        onScopeChange(['*'])
      }
    },
    [scope, onScopeChange, restoreRoot]
  )

  // A scope picked in the items list moves the chart without redrawing it on its own; a payload
  // rebuilds the tree under it, which is the one case the level has to be issued a second time.
  const dataRef = useRef(null)
  useEffect(() => {
    if (dataRef.current?.answer !== answer || dataRef.current?.answerVariants !== answerVariants) {
      dataRef.current = { answer, answerVariants }
      issuedRef.current = null
    }

    // A move of the chart's own holds only until the scope catches up; once it has, the
    // announcement is spent -- kept around, it would read the next drill back to that level
    // as still the chart's own and never issue it.
    if (null != chartRootRef.current && String(chartRootRef.current) === rootIdRef.current) {
      chartRootRef.current = null
    }

    const frame = requestAnimationFrame(() => restoreRoot(chartRef.current?.chart))

    return () => cancelAnimationFrame(frame)
  }, [scope, answer, answerVariants, restoreRoot])

  return (
    <ChartContainer
      chartRef={chartRef}
      questionId={questionId}
      questionKey={questionKey}
      qType={qType}
      chartMode={chartMode}
      chartModeTypes={[]}
      answerVariants={answerVariants}
      answer={answer}
      ignoreRefs={ignoreRefs}
      ignorePercents={ignorePercents}
      chartLoad={chartLoad}
      setRootNode={setRootNode}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      features={features}
    />
  )
}

ComboChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(['sunburst', 'treemap']).isRequired,
  answerVariants: PropTypes.array,
  // 'loading' while the answer is still being calculated: the view builds the chart under its
  // placeholder either way, and the placeholder mounts it only once the answer has arrived
  answer: answerPropType,
  scope: PropTypes.array,
  onScopeChange: PropTypes.func.isRequired,
  format: PropTypes.object,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  features: PropTypes.object
}

ComboChart.displayName = 'ComboChart'

export default ComboChart
